<!-- EditCouponUserForm.vue -->
 <script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { useCouponUserStore } from "@/stores/useCouponUserStore";

const props = defineProps({
  couponUser: {
    type: Object,
    required: true,
  },
});

const couponUserStore = useCouponUserStore();
import { useDraggableModal } from "@/composables/useDraggableModal";

const emit = defineEmits(["close", "refresh"]);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

const obj = ref({
  couponUserId: null,
  revokedAt: "",
  redeemedAt: "",
});

const isSubmitting = ref(false);

// ====================================================================
// TOAST
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 2000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

// ====================================================================
// HELPERS
// ====================================================================
function toDateTimeLocal(value) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

// Info somente-leitura (cupom/usuário já atribuídos)
const assignedCouponLabel = computed(() => {
  const cu = props.couponUser;
  if (!cu) return "—";
  return cu.couponCode ? `${cu.couponCode} (#${cu.couponId})` : `#${cu.couponId}`;
});

const assignedUserLabel = computed(() => {
  const cu = props.couponUser;
  if (!cu) return "—";
  return cu.userName ? `${cu.userName} (#${cu.userId})` : `#${cu.userId}`;
});

const assignedAtLabel = computed(() => {
  const raw = props.couponUser?.assignedAt;
  if (!raw) return "—";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
});

function fillForm(cu) {
  obj.value = {
    couponUserId: cu?.couponUserId ?? null,
    revokedAt: toDateTimeLocal(cu?.revokedAt),
    redeemedAt: toDateTimeLocal(cu?.redeemedAt),
  };
}

// Atalhos para revogar/reativar via botão
function revokeNow() {
  obj.value.revokedAt = toDateTimeLocal(new Date());
}

function clearRevoked() {
  obj.value.revokedAt = "";
}

function markRedeemedNow() {
  obj.value.redeemedAt = toDateTimeLocal(new Date());
}

function clearRedeemed() {
  obj.value.redeemedAt = "";
}

onMounted(() => {
  fillForm(props.couponUser);
});

// ====================================================================
// NAV / CLOSE
// ====================================================================
const closeModal = () => {
  if (isSubmitting.value) return;
  emit("close");
};

// ====================================================================
// VALIDATION / PAYLOAD
// ====================================================================
function validateForm() {
  if (!obj.value.couponUserId) {
    showErrorToast("ID do vínculo não informado.");
    return false;
  }
  return true;
}

function buildPayload() {
  return {
    couponUserId: Number(obj.value.couponUserId),
    revokedAt: obj.value.revokedAt || null,
    redeemedAt: obj.value.redeemedAt || null,
  };
}

// ====================================================================
// SUBMIT
// ====================================================================
async function atualizar() {
  if (isSubmitting.value) return;
  if (!validateForm()) return;

  try {
    isSubmitting.value = true;

    const payload = buildPayload();
    await couponUserStore.updateCouponUser(payload);

    showSuccessToast("Vínculo atualizado com sucesso.");

    setTimeout(() => {
      isSubmitting.value = false;
      emit("refresh");
      emit("close");
    }, 1200);
  } catch (error) {
    let msg = "Erro ao atualizar vínculo.";

    if (error.response?.data?.errors) {
      msg = Object.values(error.response.data.errors).flat().join("\n");
    } else if (error.response?.data?.message) {
      msg = error.response.data.message;
    } else if (error.message) {
      msg = error.message;
    }

    showErrorToast(msg);
    isSubmitting.value = false;
  }
}

// ====================================================================
// KEYBOARD
// ====================================================================
const handleEscKey = (event) => {
  if (event.key === "Escape" && !isSubmitting.value) {
    emit("close");
  }
};

const handleEnterKey = (event) => {
  if (event.key !== "Enter") return;

  const el = event.target;
  const tag = (el?.tagName || "").toLowerCase();
  const isEditable = !!el?.isContentEditable;

  if (tag === "textarea" || isEditable) return;
  if (tag === "button" || tag === "a") return;

  if (!isSubmitting.value) {
    event.preventDefault();
    atualizar();
  }
};

document.addEventListener("keydown", handleEscKey);
document.addEventListener("keydown", handleEnterKey);

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  document.removeEventListener("keydown", handleEnterKey);

  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div class="modal-backdrop">
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">
          Editar Vínculo de Cupom
          <span v-if="obj.couponUserId" class="modal-title-id">
            (ID: {{ obj.couponUserId }})
          </span>
        </h3>
        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="atualizar" novalidate class="modal-form">
        <div class="modal-body">
          <div class="step-content">
            <!-- Bloco somente-leitura: cupom + usuário + atribuído em -->
            <div class="readonly-card">
              <div class="readonly-row">
                <span class="readonly-label">Cupom</span>
                <span class="readonly-value mono">{{ assignedCouponLabel }}</span>
              </div>
              <div class="readonly-row">
                <span class="readonly-label">Usuário</span>
                <span class="readonly-value mono">{{ assignedUserLabel }}</span>
              </div>
              <div class="readonly-row">
                <span class="readonly-label">Atribuído em</span>
                <span class="readonly-value">{{ assignedAtLabel }}</span>
              </div>
            </div>

            <!-- Revogado em -->
            <div class="form-group">
              <label for="revokedAt" class="form-label">
                Revogado em
                <span class="field-hint-inline">
                  (vazio = vínculo ativo)
                </span>
              </label>
              <div class="input-with-actions">
                <input
                  v-model="obj.revokedAt"
                  type="datetime-local"
                  id="revokedAt"
                  class="input-base"
                />
                <button
                  type="button"
                  class="btn-mini btn-mini-warn"
                  @click="revokeNow"
                  :disabled="isSubmitting"
                  title="Definir data atual"
                >
                  Agora
                </button>
                <button
                  type="button"
                  class="btn-mini btn-mini-ghost"
                  @click="clearRevoked"
                  :disabled="isSubmitting"
                  title="Limpar (desrevogar)"
                >
                  Limpar
                </button>
              </div>
            </div>

            <!-- Resgatado em (transacional — destaque visual) -->
            <div class="form-group">
              <label for="redeemedAt" class="form-label">
                Resgatado em
                <span class="txn-hint">preenchido pelo checkout</span>
              </label>
              <div class="input-with-actions input-with-actions-txn">
                <input
                  v-model="obj.redeemedAt"
                  type="datetime-local"
                  id="redeemedAt"
                  class="input-base input-base-txn"
                />
                <button
                  type="button"
                  class="btn-mini btn-mini-warn"
                  @click="markRedeemedNow"
                  :disabled="isSubmitting"
                  title="Definir data atual"
                >
                  Agora
                </button>
                <button
                  type="button"
                  class="btn-mini btn-mini-ghost"
                  @click="clearRedeemed"
                  :disabled="isSubmitting"
                  title="Limpar"
                >
                  Limpar
                </button>
              </div>
              <p class="field-hint">
                Este campo é normalmente preenchido pelo fluxo de checkout quando
                o cupom é efetivamente consumido. Edite apenas para correção manual.
              </p>
            </div>

            <div style="flex-grow: 1"></div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="isSubmitting"
            @click="closeModal"
          >
            Cancelar
          </button>

          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitting"
          >
            <svg
              v-if="!isSubmitting"
              class="mr-1 -ml-1 w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              ></path>
            </svg>

            <svg
              v-else
              class="mr-2 -ml-1 w-5 h-5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>

            <span>
              {{ isSubmitting ? "Salvando..." : "Salvar Alterações" }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Toast -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.modal-panel {
  max-width: 34rem;
  height: 620px;
  max-height: calc(100vh - 20px - 2rem);
}

.modal-form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.modal-title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

.modal-footer {
  bottom: 0;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-height: 0;
}

.field-hint {
  font-size: 0.76rem;
  color: var(--text-muted);
  margin-top: 0.1rem;
}

.field-hint-inline {
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--text-subtle);
  font-style: italic;
}

/* ─── Destaque transacional (campos do checkout) ─── */
.txn-hint {
  font-size: 0.68rem;
  font-weight: 500;
  color: #b45309;
  background-color: var(--warning-bg);
  padding: 1px 6px;
  border-radius: 4px;
  font-style: italic;
}

.input-with-actions {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
}

.input-with-actions-txn {
  padding: 0.4rem;
  background-color: var(--warning-bg);
  border-left: 3px solid #fbbf24;
  border-radius: 6px;
}

.input-base {
  height: 36px;
}

.input-base-txn {
  flex: 1;
}

.readonly-card {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.75rem 0.9rem;
  border-radius: 12px;
  border: 1px solid var(--neutral-200);
  background-color: var(--surface-2);
}

.readonly-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
}

.readonly-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  flex-shrink: 0;
}

.readonly-value {
  font-size: 0.88rem;
  color: var(--text-dark);
  text-align: right;
  word-break: break-all;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
}

/* ─── Botões mini para ações rápidas em datas ─── */
.btn-mini {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 0.7rem;
  font-size: 0.78rem;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
  white-space: nowrap;
  transition: background-color 0.15s;
}

.btn-mini:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-mini-warn {
  background-color: var(--warning-bg);
  color: var(--warning-text);
  border-color: var(--warning-bg);
}
.btn-mini-warn:hover:not(:disabled) {
  background-color: var(--warning-bg);
}

.btn-mini-ghost {
  background-color: transparent;
  color: var(--text-muted);
  border-color: var(--neutral-400);
}
.btn-mini-ghost:hover:not(:disabled) {
  background-color: var(--surface-3);
}

</style>